import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { AppFeatureExtensions } from '@app/app-feature-extensions.config';
import { appConfig } from '@app/app.config';
import { getAssociationLabel } from '@collections/association-label';
import { AssociationByCandidateIdJobIdObj } from '@collections/associations';
import { CandidateHiringManagerStatusObj } from '@collections/hiring-manager-status';
import { getJob } from '@collections/jobs';
import { getUsersLoggedIn } from '@collections/users';
import {
  ChangeCandidateStatusModalComponent,
  ChangeCandidateStatusModalData,
} from '@modules/assigns/components/change-candidate-status-modal/change-candidate-status-modal.component';
import {
  HiringManagerSubmitModalComponent,
  HiringManagerSubmitModalData,
} from '@modules/assigns/components/hiring-manager-submit-modal/hiring-manager-submit-modal.component';
import {
  getNextAvailablePhases,
  getPreviousAvailablePhases,
  getAssociationStatusConfigurationsShortList,
  getMoveForwardPhases,
  getAssociationStatusConfigurationsRejected,
  SelectedPhaseObj,
} from '@modules/assigns/store';
import { CandidatesConfig } from '@modules/candidates/candidates.abstract.config';
import { getCandidateById } from '@modules/candidates/store';
import { removeCandidatesFromTalentPool } from 'mojo-talent-pools';
import { concatLatestFrom } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import {
  AssociationLabel,
  AssociationStatusConfiguration,
  User,
  Job,
  Candidate,
  Association,
} from '@shared';
import {
  AddProspectModalComponent,
  AddProspectModalData,
} from '@shared/add-prospect/components/add-prospect-modal/add-prospect-modal.component';
import { DangerAlert } from '@shared/alert';
import {
  AddCandidateModalComponent,
  AddCandidateModalData,
} from '@shared/candidate/side-nav-candidate/components/talent-pool-add-candidate-modal/add-candidate-modal.component';
import {
  InviteToApplyAutocompleteModalComponent,
  InviteToApplyAutocompleteModalData,
} from '@shared/invite-to-apply/invite-to-apply-autocomplete-modal/invite-to-apply-autocomplete-modal.component';
import { InviteToApplyModalComponent } from '@shared/invite-to-apply/modal/invite-to-apply-modal.component';
import { hcmJobPostShowError } from '@shared/invite-to-apply/store/invite-to-apply.actions';
import {
  getJobPostUrlSelector,
  getJobPostUrlErrorSelector,
} from '@shared/invite-to-apply/store/invite-to-apply.selectors';
import { noteRejectedModalShow } from '@shared/modal';
import { InviteToApply } from '@shared/models/invite-to-apply';
import { JobLocation } from '@shared/models/job-location';
import { JobStatusEnum } from '@shared/models/job-statuses';
import { MojoFeatureExtensionsService } from 'mojo-feature-extensions';
import { MojoPrivilegesService } from 'mojo-privileges';
import { Observable, combineLatest } from 'rxjs';
import { map, switchMap, take, filter, takeWhile } from 'rxjs/operators';
import {
  RemoveTalentPoolModalComponent,
  RemoveTalentPoolModalComponentData,
} from '@modules/talent-pools-page/components/remove-talent-pool-modal/remove-talent-pool-modal';
import { GreenhouseIntegrationService } from '@shared/services/greenhouse-integration.service';

const mediumSizeModal = 'medium-size-modal';
const moveForward = 'Move forward';
const trending = 'trending_up';

@Injectable({ providedIn: 'root' })
export class DropdownMenuWithIconsService {
  isHiringManagerFlow$: Observable<boolean> = this.featureExtensionsService.hasFeatureExtension(
    AppFeatureExtensions.HIRING_MANAGER_FLOW,
  );

  constructor(
    private dialog: MatDialog,
    private privilegesService: MojoPrivilegesService,
    private featureExtensionsService: MojoFeatureExtensionsService,
    private candidatesConfig: CandidatesConfig,
    private store: Store,
    private router: Router,
    public greenhouseIntegrationService: GreenhouseIntegrationService,
  ) {}

  public shouldDisplaySubmitToHiringManager(
    jobId: number,
    candidateId: number,
  ): Observable<boolean> {
    return combineLatest([
      this.isDirectApplicant(jobId, candidateId),
      this.isHiringManagerFlow$,
      this.store
        .select(
          CandidateHiringManagerStatusObj.getCandidateHiringManagerStatus({ candidateId, jobId }),
        )
        .pipe(map((candidateJobStatus) => !candidateJobStatus)),
    ]).pipe(map((array) => array.every((val) => val === true)));
  }

  public submitToHiringManager(jobId: number, candidateId: number) {
    this.dialog.open<HiringManagerSubmitModalComponent, HiringManagerSubmitModalData>(
      HiringManagerSubmitModalComponent,
      {
        panelClass: mediumSizeModal,
        disableClose: true,
        data: {
          headerText: 'Submit a candidate to Hiring Manager',
          subheaderText: 'Share some highlights you see in this candidate',
          candidateId,
          jobId,
        },
      },
    );
  }

  public shouldDisplayMoveForwardForShortlisted(
    jobId: number,
    candidateId: number,
  ): Observable<boolean> {
    return combineLatest([
      this.isInShortlist(candidateId, jobId),
      this.isInRejected(candidateId, jobId),
    ]).pipe(map(([shortlist, rejected]) => shortlist && !rejected));
  }

  public shouldDisplayMoveForwardForDirectApplicants(
    jobId: number,
    candidateId: number,
  ): Observable<boolean> {
    return combineLatest([
      this.isDirectApplicant(jobId, candidateId),
      this.isInShortlist(candidateId, jobId),
      this.isInRejected(candidateId, jobId),
    ]).pipe(map(([direct, shortlist, rejected]) => direct && !shortlist && !rejected));
  }

  public moveForwardForDirectApplicants(
    jobId: number,
    candidateId: number,
    totalMatchesAssign: boolean,
  ) {
    combineLatest([
      this.store.select(
        AssociationByCandidateIdJobIdObj.getAssociationByCandidateIdAndJobId(candidateId, jobId),
      ),
      this.featureExtensionsService.hasFeatureExtension(
        AppFeatureExtensions.ASSOCIATION_FORWARD_ONLY,
      ),
    ])
      .pipe(
        switchMap(([association, moveForwardOnly]) =>
          this.store
            .select(getMoveForwardPhases, {
              jobId,
              currentAssociation: association,
              moveForwardOnly,
            })
            .pipe(
              map(
                ([phase, availableSteps]: [
                  SelectedPhaseObj,
                  AssociationStatusConfiguration[],
                ]) => ({
                  phase,
                  availableSteps,
                  moveForwardOnly,
                }),
              ),
            ),
        ),
        take(1),
      )
      .subscribe(({ phase, availableSteps, moveForwardOnly }) => {
        this.dialog.open<ChangeCandidateStatusModalComponent, ChangeCandidateStatusModalData>(
          ChangeCandidateStatusModalComponent,
          {
            panelClass: mediumSizeModal,
            disableClose: true,
            data: {
              headerText: 'Moving to shortlist',
              candidateId,
              jobId,
              initialPhase: phase.status,
              initialState: phase.subStatus,
              availableSteps,
              totalMatchesAssign,
              actions: [],
              moveForwardOnly,
            },
          },
        );
      });
  }

  public isDirectApplicant(jobId: number, candidateId: number): Observable<boolean> {
    return this.store
      .select(getAssociationLabel, { candidateId, jobId })
      .pipe(map((label) => label === AssociationLabel.DIRECT));
  }

  public openRejectModal(jobId: number, candidateId: number, statusId: string) {
    this.store.dispatch(
      noteRejectedModalShow({
        payload: {
          jobId,
          candidateId,
          subStatus: statusId,
        },
      }),
    );
  }

  public openInviteToApplyForJob(candidateId: number, jobId: number) {
    const urlError = this.store.select(getJobPostUrlErrorSelector, { jobId });
    combineLatest([
      this.store.select(getUsersLoggedIn),
      this.store.select(getJobPostUrlSelector, { jobId }).pipe(filter((url) => !!url)),
      this.store.select(getCandidateById, { id: candidateId }),
      this.store.select(getJob(jobId)),
    ])
      .pipe(
        map(
          ([user, url, candidate, job]: [User, string, Candidate, Job]) =>
            ({
              candidateName: candidate.firstName + ' ' + candidate.lastName,
              url,
              senderEmail: user.email,
              jobName: job.title,
              jobLocation: this.getJobLocation(job.locations),
              recipientEmail: candidate.emailId,
              candidateId: candidate.id,
              jobId: job.id,
            } as InviteToApply),
        ),
        takeWhile((inviteToApply) => !inviteToApply?.url || !urlError, true),
        take(1),
      )
      .subscribe((inviteToApply) => {
        if (inviteToApply?.recipientEmail === '') {
          this.store.dispatch(
            new DangerAlert(
              'Selected candidate does not have an email address set. Unable to send invitation to apply.',
            ),
          );
        } else {
          if (inviteToApply && inviteToApply.url) {
            this.dialog.open<InviteToApplyModalComponent, InviteToApply>(
              InviteToApplyModalComponent,
              {
                panelClass: [mediumSizeModal, 'invite-to-apply-dialog'],
                data: inviteToApply,
              },
            );
          } else if (urlError) {
            this.store.dispatch(hcmJobPostShowError({ jobId: jobId.toString() }));
          }
        }
      });
  }

  public openAddAsProspectModal(candidateId: number) {
    this.dialog.open<AddProspectModalComponent, AddProspectModalData>(AddProspectModalComponent, {
      panelClass: mediumSizeModal,
      disableClose: true,
      data: {
        candidateId,
      },
    });
  }

  public openInviteToApplyModal(candidateId: number) {
    this.dialog.open<InviteToApplyAutocompleteModalComponent, InviteToApplyAutocompleteModalData>(
      InviteToApplyAutocompleteModalComponent,
      {
        panelClass: mediumSizeModal,
        disableClose: true,
        data: {
          candidateId,
        },
      },
    );
  }

  public isPassiveApplicant(candidateId: number, jobId: number) {
    return this.store
      .select(getAssociationLabel, { candidateId, jobId })
      .pipe(map((label) => label === AssociationLabel.PASSIVE));
  }

  public isJobActive(jobId: number) {
    return this.store
      .select(getJob(jobId))
      .pipe(map((job) => job.status !== JobStatusEnum.Canceled));
  }

  public shouldDisplayInviteToApply(): Observable<boolean> {
    return combineLatest([
      this.featureExtensionsService.hasFeatureExtension(AppFeatureExtensions.FMI_INVITE_TO_APPLY),
      this.privilegesService.hasPrivilege(
        this.candidatesConfig.privileges.candidates.inviteToApply,
      ),
    ]).pipe(map(([featureExtension, privlege]) => featureExtension && privlege));
  }

  public openAddToTalentPoolModal(candidateId: number) {
    this.dialog.open<AddCandidateModalComponent, AddCandidateModalData>(
      AddCandidateModalComponent,
      {
        panelClass: mediumSizeModal,
        disableClose: true,
        data: {
          candidateIds: [candidateId],
        },
      },
    );
  }

  public hasTalentPoolExtensionAndViewPrivilege(): Observable<boolean> {
    return combineLatest([
      this.featureExtensionsService.hasFeatureExtension(AppFeatureExtensions.TALENT_POOLS),
      this.privilegesService.hasPrivilege(appConfig.privileges.talentPools.view),
    ]).pipe(
      map(([hasTalentPoolFe, hasTalentPoolPrivilege]) => hasTalentPoolFe && hasTalentPoolPrivilege),
    );
  }

  public openMoveForwardModal(candidateId: number) {
    this.router.navigate([{ outlets: { modal: ['candidate', candidateId] } }], {
      skipLocationChange: true,
    });
  }

  public openMoveForwardForShortlistModal(candidateId: number, jobId: number) {
    this.store
      .select(
        AssociationByCandidateIdJobIdObj.getAssociationByCandidateIdAndJobId(candidateId, jobId),
      )
      .pipe(
        concatLatestFrom((association) => this.getAvailablePhases$(association)),
        take(1),
      )
      .subscribe(([association, availablePhases]) => {
        this.dialog.open<ChangeCandidateStatusModalComponent, ChangeCandidateStatusModalData>(
          ChangeCandidateStatusModalComponent,
          {
            panelClass: 'medium-size-modal',
            disableClose: true,
            data: {
              jobId: association.reqId,
              candidateId: association.candidateId,
              initialPhase: association.status,
              initialState: association.subStatus,
              availableSteps: availablePhases,
            },
          },
        );
      });
  }

  public isProspect(candidateId: number, jobId: number) {
    return this.store
      .select(getAssociationLabel, { candidateId, jobId })
      .pipe(map((label) => label === AssociationLabel.PROSPECT));
  }

  public isInShortlist(candidateId: number, jobId: number) {
    return combineLatest([
      this.store.select(getAssociationStatusConfigurationsShortList, { jobId }),
      this.store.select(
        AssociationByCandidateIdJobIdObj.getAssociationByCandidateIdAndJobId(candidateId, jobId),
      ),
    ]).pipe(
      map(([statuses, association]) => {
        for (const status of statuses) {
          if (association?.status === status.id) {
            return true;
          }
        }
        return false;
      }),
    );
  }

  public isInRejected(candidateId: number, jobId: number) {
    return combineLatest([
      this.store.select(getAssociationStatusConfigurationsRejected, { jobId }),
      this.store.select(
        AssociationByCandidateIdJobIdObj.getAssociationByCandidateIdAndJobId(candidateId, jobId),
      ),
    ]).pipe(
      map(([statuses, association]) => {
        for (const status of statuses) {
          if (association?.subStatus === status.id) {
            return true;
          }
        }
        return false;
      }),
    );
  }

  public shouldDisplayAssignJobsToCandidate(): Observable<boolean> {
    return this.privilegesService.hasPrivilege(
      this.candidatesConfig.privileges.associate.jobs_to_candidate,
    );
  }

  public shouldDisplayAddAsAProspect(): Observable<boolean> {
    return this.featureExtensionsService.hasFeatureExtension(AppFeatureExtensions.PROSPECT);
  }

  private getJobLocation(locations: JobLocation[]): string {
    let locationResult = '';
    locations.forEach((location) => {
      if (location) {
        if (location.city !== null) {
          locationResult = locationResult + location.city + ', ';
        }
        if (location.countryName !== null) {
          locationResult = locationResult + location.countryName + '. ';
        }
      }
    });
    return locationResult.replace(/,\s*$/, '');
  }

  private getAvailablePhases$(
    association: Association,
  ): Observable<AssociationStatusConfiguration[]> {
    return combineLatest([
      this.store.select(getNextAvailablePhases, {
        jobId: association?.reqId,
        selectedPhaseId: association.status,
      }),
      this.store.select(getPreviousAvailablePhases, {
        jobId: association?.reqId,
        selectedPhaseId: association.status,
      }),
      this.featureExtensionsService.hasFeatureExtension(
        AppFeatureExtensions.ASSOCIATION_FORWARD_ONLY,
      ),
    ]).pipe(
      map(([nextPhases, previousPhases, moveForwardOnly]) =>
        moveForwardOnly ? nextPhases : [...previousPhases, ...nextPhases],
      ),
    );
  }

  openRemoveTalentPoolModal(talentPoolId, page, numberOfElements) {
    const pageToSend = numberOfElements === 1 && page > 0 ? --page : page;
    this.dialog.open(RemoveTalentPoolModalComponent, {
      width: '270px',
      autoFocus: false,
      data: {
        talentPoolId,
        page: pageToSend,
      } as RemoveTalentPoolModalComponentData,
    });
  }

  getMoveForwardForDirectApplicantsForSpecificJob(jobId: number) {
    return {
      icon: trending,
      text: moveForward,
      callback: (candidateId) => this.moveForwardForDirectApplicants(jobId, candidateId, true),
      shouldDisplay: (candidateId) =>
        this.shouldDisplayMoveForwardForDirectApplicants(jobId, candidateId),
    };
  }

  getMoveForwardForShortlistForSpecificJob(jobId: number) {
    return {
      icon: trending,
      text: moveForward,
      callback: (candidateId) => this.moveForwardForDirectApplicants(jobId, candidateId, false),
      shouldDisplay: (candidateId) =>
        this.shouldDisplayMoveForwardForShortlisted(jobId, candidateId),
    };
  }

  getMoveForwardForProspectsForSpecificJob(jobId: number) {
    return {
      icon: trending,
      text: moveForward,
      callback: (candidateId) => this.moveForwardForDirectApplicants(jobId, candidateId, false),
      shouldDisplay: (candidateId) =>
        combineLatest([
          this.isProspect(candidateId, jobId),
          this.featureExtensionsService.hasFeatureExtension(
            AppFeatureExtensions.FMI_INVITE_TO_APPLY,
          ),
        ]).pipe(map(([prospect, fmiInviteToApply]) => prospect && !fmiInviteToApply)),
    };
  }

  getMoveForwardForAnyJob() {
    return {
      icon: trending,
      text: moveForward,
      callback: (candidateId) => this.openMoveForwardModal(candidateId),
      shouldDisplay: () => this.shouldDisplayAssignJobsToCandidate(),
      bottomLine: true,
    };
  }

  getSubmitToHiringManagerForSpecificJob(jobId: number) {
    return {
      icon: 'forward_to_inbox',
      text: 'Submit to hiring manager',
      callback: (candidateId) => this.submitToHiringManager(jobId, candidateId),
      shouldDisplay: (candidateId) => this.shouldDisplaySubmitToHiringManager(jobId, candidateId),
      bottomLine: true,
    };
  }

  getInviteToApplyForSpecificJob(jobId: number) {
    return {
      icon: 'campaign',
      text: 'Invite to apply',
      callback: (candidateId) => this.openInviteToApplyForJob(candidateId, jobId),
      shouldDisplay: (candidateId) =>
        this.shouldDisplayInviteToApply().pipe(
          switchMap((shouldDisplay) =>
            combineLatest([
              this.isProspect(candidateId, jobId),
              this.isPassiveApplicant(candidateId, jobId),
              this.isJobActive(jobId),
            ]).pipe(
              map(
                ([isProspect, activeOrPassive, activeJob]) =>
                  shouldDisplay && (isProspect || activeOrPassive) && activeJob,
              ),
            ),
          ),
        ),
    };
  }

  getInviteToApplyForAnyJob() {
    return {
      icon: 'campaign',
      text: 'Invite to apply',
      callback: (candidateId) => this.openInviteToApplyModal(candidateId),
      shouldDisplay: () => this.shouldDisplayInviteToApply(),
    };
  }

  getAddAsProspectForAnyJob() {
    return {
      icon: 'assignment_ind',
      text: 'Add as a prospect',
      callback: (candidateId) => this.openAddAsProspectModal(candidateId),
      shouldDisplay: () => this.shouldDisplayAddAsAProspect(),
    };
  }

  getAddToTalentPoolForAnyJob() {
    return {
      icon: 'person_add_alt',
      text: 'Add to talent pool',
      callback: (candidateId) => this.openAddToTalentPoolModal(candidateId),
      shouldDisplay: () => this.hasTalentPoolExtensionAndViewPrivilege(),
      bottomLine: true,
    };
  }

  pathRemoveCandidateFromTalentPool() {
    return {
      icon: 'delete',
      text: 'Remove from talent pool',
      callback: (candidateId, parameters) =>
        this.store.dispatch(
          removeCandidatesFromTalentPool({
            payload: { candidateIds: [candidateId], talentPoolId: parameters.talentPoolId },
          }),
        ),
      shouldDisplay: () =>
        this.hasTalentPoolExtensionAndViewPrivilege() &&
        this.greenhouseIntegrationService.isPoolSynchronizationOff(),
    };
  }

  removeTalentPool() {
    return {
      icon: 'delete',
      text: 'Delete',
      callback: (talentPoolId, parameters) =>
        this.openRemoveTalentPoolModal(talentPoolId, parameters.page, parameters.numberOfElements),
      shouldDisplay: () =>
        this.hasTalentPoolExtensionAndViewPrivilege() &&
        this.greenhouseIntegrationService.isPoolSynchronizationOff(),
    };
  }
}
